import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';
import { PAY_TYPE_CODE_MAP } from '@/config/constant';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import { updateRefundInfo } from '@/redux/actions/bill/refund';
import EditableTable from '@/components/EditableTable';
import { parseFen2Currency } from '@/utils/number';
import usePortalCalculator from '@/components/RefoundCalculatorModal';

import { RefundItem } from '../interface';
import { BILL_TYPE_MAP } from '../config';
import commonStyles from '../common.module.scss';
import styles from './index.module.scss';

function RoomRate(props) {
  const { refundAmounts, refundInfoLoading, refundInfo } = props;

  const [undistributedRoomRate, setUndistributedRoomRate] = useState<number>(0);

  // 渠道退款计算器
  const calculatorPortal = usePortalCalculator();

  // 计算未分配退款
  const calcRestRoomRate = (roomRate: Array<RefundItem>, invalidRow?: RefundItem) => {
    const totalRoomRate = roomRate.reduce((prev, cur) => {
      return prev + (cur.no !== invalidRow?.no ? cur.defaultRefundAmount : 0);
    }, 0);

    // 整数计算，不用考虑浮点数溢出
    const rest = refundAmounts?.roomRate - totalRoomRate;
    setUndistributedRoomRate(rest);

    // callback parent
    props.setRestRoomRate(rest);
  };

  // 计算当前账目的剩余可退金额
  const calcRestRefundAmount = (sourceList: Array<RefundItem>, curItem: RefundItem) => {
    // 其他退款账目退款金额总和
    const restTotalRefundAmount = sourceList.reduce((prev, cur) => {
      return prev + (cur.no !== curItem.no ? cur.defaultRefundAmount : 0);
    }, 0);

    return refundAmounts?.roomRate > restTotalRefundAmount
      ? refundAmounts?.roomRate - restTotalRefundAmount
      : 0;
  };

  // 退款金额输入更新回调
  const saveModifiedList = (modifiedList: Array<RefundItem>, curItem: RefundItem) => {
    // 输入的退款金额有问题（v1.10.1新增输入校验状态）
    if (curItem?.amountInvalid) {
      calcRestRoomRate(refundInfo?.roomRate, curItem);
      return;
    }

    const restRefundAmount = calcRestRefundAmount(modifiedList, curItem);

    const roomRate = modifiedList.map((item) => {
      if (item.no === curItem.no) {
        // 计算最大可退金额
        const maxRefundAmount = Math.min(
          restRefundAmount,
          item.amount,
          item.defaultRefundAmount,
          item.refundableAmount,
        );
        return {
          ...item,
          defaultRefundAmount: maxRefundAmount,
        };
      }
      return item;
    });

    // 更新列表
    props.updateRefundInfo({
      refundInfo: {
        ...refundInfo,
        roomRate,
      },
    });
  };

  // 打开渠道退款计算算器
  const openCalculator = (modifiedList: Array<RefundItem>, curItem: RefundItem) => {
    // 输入的退款金额有问题（v1.10.1新增输入校验状态）
    if (curItem?.amountInvalid) {
      calcRestRoomRate(refundInfo?.roomRate, curItem);
      return;
    }
    const restRefundAmount = calcRestRefundAmount(modifiedList, curItem);
    const params = {
      channelNo: curItem?.channelNo, // 渠道订单号
      payChanel: curItem.paymentType, // 支付渠道
      maxRefoundAmount: restRefundAmount, // 最大可退金额
    };
    calculatorPortal.showDrawer({
      title: intl.get('bill_cross_refound_calculator'),
      cancelText: intl.get('app_confirm_close_btn'),
      ...params,
      onConfirm: async (refundAmount: any) => {
        // refundAmount: 500.00
        const roomRate = modifiedList.map((item) => {
          if (item.no === curItem.no) {
            return {
              ...item,
              defaultRefundAmount: Number(refundAmount) * 100,
            };
          }
          return item;
        });
        // 更新列表
        props.updateRefundInfo({
          refundInfo: {
            ...refundInfo,
            roomRate,
          },
        });
        calculatorPortal.closeDrawer();
      },
      onCancel: () => {
        calculatorPortal.closeDrawer();
      },
    });
  };

  // init
  useEffect(() => {
    if (refundInfo.roomRate) {
      calcRestRoomRate(refundInfo.roomRate);
    }
  }, [refundInfo.roomRate]);

  // unmount
  useEffect(() => {
    return () => {
      // reset states
      setUndistributedRoomRate(0);
    };
  }, []);

  const roomRateColumns = [
    {
      title: intl.get('bill_refound_res_room_no'),
      dataIndex: 'roomNo',
      key: 'roomNo',
      width: 80,
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_entry_bill_type'),
      dataIndex: 'postType',
      key: 'postType',
      width: 100,
      render: (type) => (BILL_TYPE_MAP[type] ? BILL_TYPE_MAP[type] : '-'),
    },
    {
      title: intl.get('bill_refound_res_bill_subject'),
      dataIndex: 'subjectName',
      key: 'subjectName',
      width: 120,
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_pay_channel'),
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 100,
      render: (type) => (PAY_TYPE_CODE_MAP[type] ? PAY_TYPE_CODE_MAP[type] : '-'),
    },
    {
      title: intl.get('bill_refound_res_detail'),
      dataIndex: 'detail',
      key: 'detail',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_pay'),
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
    {
      title: intl.get('bill_refound_res_pay_time'),
      dataIndex: 'paymentTime',
      key: 'paymentTime',
      width: 200,
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('bill_cross_refound_avalible_amount'),
      dataIndex: 'refundableAmount',
      key: 'refundableAmount',
      width: 100,
      align: 'right',
      render: (value) => parseFen2Currency(value),
    },
    {
      title: intl.get('bill_cross_refound_amount'),
      dataIndex: 'defaultRefundAmount',
      key: 'defaultRefundAmount',
      width: 250,
      align: 'right',
      editable: true,
      isPriceData: true,
      maxValue: 'refundableAmount',
      render: (value) => parseFen2Currency(value || 0),
    },
  ];

  return (
    <>
      {refundInfo.roomRate && refundInfo.roomRate.length > 0 ? (
        <dl className={styles['refund-block']}>
          <dt className={commonStyles['bill-block-title']}>
            {intl.get('bill_cross_refound_amount')}
          </dt>
          <dd>
            <EditableTable
              rowKey="no"
              loading={refundInfoLoading}
              dataSource={refundInfo.roomRate}
              columns={roomRateColumns}
              saveDataList={(list: Array<RefundItem>, item: RefundItem) =>
                saveModifiedList(list, item)
              }
              openCalculator={(list: Array<RefundItem>, item: RefundItem) =>
                openCalculator(list, item)
              }
              pagination={false}
            />
          </dd>
          <dd>
            <ul>
              <li>
                <label className="text-colon">
                  {intl.get('bill_refund_should_refund_room_fee')}
                </label>
                <span className="amount-refund">{parseFen2Currency(refundAmounts?.roomRate)}</span>
              </li>
              <li>
                <label className="text-colon">{intl.get('bill_refund_unallocated_refund')}</label>
                <span>{parseFen2Currency(undistributedRoomRate)}</span>
              </li>
            </ul>
          </dd>
        </dl>
      ) : (
        ''
      )}
    </>
  );
}

const mapStateToProps = (state) => {
  const { refundInfoLoading, refundInfo } = state.refund;
  return { refundInfoLoading, refundInfo };
};

export default connect(mapStateToProps, { updateRefundInfo })(RoomRate);
